import React, { useEffect, useState } from 'react';
import './AdminPanel.css';

const API_URL = '/api/v1/super';

const AdminPanel = () => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [showUsers, setShowUsers] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const getAuthHeaders = (includeJson = false) => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Your session expired. Please log in again.');
    }

    return {
      'Authorization': `Bearer ${token}`,
      ...(includeJson ? { 'Content-Type': 'application/json' } : {}),
    };
  };

  const toggleSubscribe = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/users/${userId}/subscribe`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to toggle');
      const data = await res.json();
      setUsers(prev => prev.map(u =>
        u.id === data.id ? { ...u, email_subscribed: data.email_subscribed } : u
      ));
    } catch (e) {
      setError(e.message);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await fetch(`${API_URL}/users`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to fetch users');
      const data = await res.json();
      setUsers(data);
      setShowUsers(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) {
      setError('Subject and body are required');
      return;
    }

    const subscribedCount = users.filter(u => u.email_subscribed && !u.is_deactivated).length;
    if (!window.confirm(`Send this email to ${subscribedCount} subscribed user${subscribedCount !== 1 ? 's' : ''}?`)) return;

    setSending(true);
    setError('');
    setResult(null);

    try {
      const res = await fetch(`${API_URL}/mass-email`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ subject, body }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to send');
      }

      const job = await res.json();
      setSubject('');
      setBody('');
      await pollJob(job);
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  const pollJob = async (job) => {
    setResult(job);
    while (job.status === 'running') {
      await new Promise(resolve => setTimeout(resolve, 10000));
      const statusRes = await fetch(`${API_URL}/mass-email/status`, {
        headers: getAuthHeaders(),
      });
      if (!statusRes.ok) continue;
      job = await statusRes.json();
      setResult(job);
    }
  };

  useEffect(() => {
    const resumeRunningJob = async () => {
      try {
        const res = await fetch(`${API_URL}/mass-email/status`, { headers: getAuthHeaders() });
        if (!res.ok) return;
        const job = await res.json();
        if (job.status !== 'running') return;
        setSending(true);
        await pollJob(job);
      } catch (e) {
        setError(e.message);
      } finally {
        setSending(false);
      }
    };
    resumeRunningJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="admin-panel-wrapper">
      <div className="dashboard-container">
        <div className="dash-card admin-panel">
          <div className="dash-card-header">
            <h3 className="dash-card-title">
              <span className="card-icon">🔧</span>
              Super Admin
            </h3>
            <button
              className="btn btn-secondary"
              onClick={fetchUsers}
              disabled={loadingUsers}
            >
              {loadingUsers ? 'Loading...' : showUsers ? 'Refresh Users' : 'View Users'}
            </button>
          </div>

          {showUsers && (
            <div className="admin-users-list">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Joined</th>
                    <th>Status</th>
                    <th>Subscribed</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td>{u.id}</td>
                      <td>{u.name}</td>
                      <td>{u.email}</td>
                      <td>{new Date(u.created_at).toLocaleDateString()}</td>
                      <td>{u.is_deactivated ? '❌ Deactivated' : '✓ Active'}</td>
                      <td>
                        <button
                          className={`btn-subscribe ${u.email_subscribed ? 'subscribed' : 'unsubscribed'}`}
                          onClick={() => toggleSubscribe(u.id)}
                          title={u.email_subscribed ? 'Click to unsubscribe' : 'Click to subscribe'}
                        >
                          {u.email_subscribed ? '✓ Yes' : '✗ No'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="admin-user-count">{users.length} total users</div>
            </div>
          )}

          <div className="admin-email-section">
            <h4>Send Mass Email</h4>
            <p className="admin-email-note">
              Sends to subscribed users only. Each email includes an unsubscribe link.
              Body supports HTML (e.g. &lt;p&gt;, &lt;strong&gt;, &lt;br&gt;).
            </p>

            <form onSubmit={handleSend}>
              <div className="form-group">
                <label className="form-label">Subject</label>
                <input
                  type="text"
                  className="form-input"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  placeholder="e.g. We're back online!"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Body (HTML)</label>
                <textarea
                  className="form-textarea"
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  placeholder="<p>Hi! We wanted to let you know...</p>"
                  rows={6}
                />
              </div>

              {error && <div className="alert alert-error">{error}</div>}

              {result && (
                <div className={`alert ${result.status === 'aborted' ? 'alert-error' : 'alert-success'}`}>
                  {result.status === 'running' &&
                    `Sending at up to ${result.hourly_limit} emails/hour (about ${result.estimated_minutes} minutes total; you can close this page). `}
                  Sent {result.sent} of {result.total} emails.
                  {result.failed > 0 && ` ${result.failed} failed.`}
                  {result.abort_reason && ` ${result.abort_reason}`}
                  {result.errors?.length > 0 && (
                    <ul>
                      {result.errors.map((err, i) => (
                        <li key={i}>{err.email}: {err.error}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary btn-full"
                disabled={sending}
              >
                {sending ? 'Sending...' : 'Send to All Users'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
